import { useShipmentFiltersContext } from "hooks/useShipmentFiltersContext";

import { DEFAULT_SHIPMENT_STATUSES } from "./constants";
import { ShipmentsFilter } from "./ShipmentsFilter";

interface Props {
  isOpen: boolean;
  onOpen: VoidFunction;
  onClose: VoidFunction;
}

export function ShipmentsFilterContainer(props: Props) {
  const { isOpen, onOpen, onClose } = props;
  const {
    updateOrigin,
    updateDestination,
    updateEquipmentType,
    updateServiceType,
    updateShipmentStatus,
    updateIncludeCompanyShipments,
    updateIncludeBranchShipments,
    updateBranches,
    updateShippers,
    filters,
    refreshShipments,
    clearFilters,
  } = useShipmentFiltersContext();

  const filterState: ShipmentsFilterState = {
    equipmentTypes: filters?.equipment_types ?? [],
    services: filters?.services ?? [],
    commodity: filters?.commodity ?? null,
    shipmentStatus: filters?.status ?? DEFAULT_SHIPMENT_STATUSES,
    origin: {
      provinces: filters?.origin?.provinces ?? [],
      countries: filters?.origin?.countries ?? [],
    },
    destination: {
      provinces: filters?.destination?.provinces ?? [],
      countries: filters?.destination?.countries ?? [],
    },
    orderBy: "created_at",
    sortOrder: "DESC",
    shipmentVisibility: "public",
    shipmentRelation: "public",
    shipmentName: filters?.shipmentName ?? "",
    shipmentTypes: filters?.shipmentTypes ?? [],
    includeBranchShipments: filters?.includeBranchShipments ?? false,
    includeCompanyShipments: filters?.includeCompanyShipments ?? false,
    branches: filters?.branches ?? [],
    shippers: filters?.shippers ?? [],
    shipperBusinessType: null,
  };

  const handleLocationChange = (
    key: keyof Location,
    addressKey: keyof ShipmentLocationFilter,
    value: string[],
  ): void => {
    if (key === "origin") {
      updateOrigin(addressKey, value);
    } else if (key === "destination") {
      updateDestination(addressKey, value);
    }
  };

  const handleEquipmentTypeChange = (value: string[]): void => {
    updateEquipmentType(value);
  };

  const handleServiceTypeChange = (value: string[]): void => {
    updateServiceType(value);
  };

  const handleShipmentStatusChange = (value: string[]): void => {
    updateShipmentStatus(value as ShipmentStatusValue[]);
  };

  const toggleFilterTools = (): void => {
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  };

  const handleIncludeCompanyShipmentsChange = (value: boolean): void => {
    updateIncludeCompanyShipments(value);
    if (value) {
      updateBranches([]);
      updateShippers([]);
    }
  };

  const handleIncludeBranchShipmentsChange = (value: boolean): void => {
    updateIncludeBranchShipments(value);
    if (value) {
      updateBranches([]);
      updateShippers([]);
    }
  };

  const handleShippersChange = (value: string[]): void => {
    updateShippers(value);
    if (value.length > 0) {
      updateIncludeCompanyShipments(false);
      updateIncludeBranchShipments(false);
    }
  };

  const handleBranchesChange = (value: string[]): void => {
    updateBranches(value);
    if (value.length > 0) {
      updateIncludeCompanyShipments(false);
      updateIncludeBranchShipments(false);
      updateShippers([]);
    }
  };

  const handleClearFilters = () => {
    clearFilters();
    refreshShipments();
  };

  return (
    <ShipmentsFilter
      filters={filterState}
      filterToolsAreVisible={isOpen}
      toggleFilterTools={toggleFilterTools}
      onLocationChanged={handleLocationChange}
      onEquipmentTypeChanged={handleEquipmentTypeChange}
      onServiceTypeChanged={handleServiceTypeChange}
      onShipmentStatusChanged={handleShipmentStatusChange}
      updateIncludeCompanyShipments={handleIncludeCompanyShipmentsChange}
      updateIncludeBranchShipments={handleIncludeBranchShipmentsChange}
      updateBranches={handleBranchesChange}
      updateShippers={handleShippersChange}
      onClearFilters={handleClearFilters}
    />
  );
}
